import { TextareaAutosize } from '@material-ui/core';
import { Editor, Extensions } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import ListKeymap from '@tiptap/extension-list-keymap';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import { SlashMenu as AssemblySlashMenu } from 'copilot-design-system';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { ChangeEventHandler } from 'react';

import { SlashCommand } from 'src/components/tiptap/custom-extensions/SlashCommand';
import { SlashMenuRenderer } from 'src/components/tiptap/custom-extensions/SlashMenuRenderer';
import { EditorMenu } from 'src/components/tiptap/editor-menu/EditorMenu';
import { RichTextInput } from 'src/components/tiptap/RichTextInput';
import { RichTextStyles } from 'src/components/tiptap/RichTextStyles';
import { SlashMenu } from 'src/components/tiptap/slash-menu/SlashMenu';

import { getSlashMenuSuggestionItems } from './utils';

type EditorEvent = 'create' | 'update';

interface NoteEditorProps {
  title: string;
  onTitleChange: ChangeEventHandler<HTMLTextAreaElement>;
  content: string;
  onContentChange: ({
    html,
    plaintext,
    event,
  }: {
    html: string;
    plaintext: string;
    event: EditorEvent;
  }) => void;
  onLinkPopoverOpenChange?: (isOpen: boolean) => void;
}

export const noteEditorExtensions: Extensions = [
  StarterKit.configure({
    heading: {
      HTMLAttributes: {
        'data-typography': 'heading',
      },
    },
  }),
  Placeholder.configure({
    emptyEditorClass:
      'before:content-[attr(data-placeholder)] before:float-left before:text-[#9B9FA3] before:h-0 before:pointer-events-none',
    placeholder: 'Write something...',
  }),
  Underline,
  Link,
  ListKeymap,
];

export const NoteEditor = ({
  title,
  onTitleChange,
  content,
  onContentChange,
  onLinkPopoverOpenChange,
}: NoteEditorProps) => {
  const EnableNewEditorFormatter = useFeatureFlagEnabled(
    'new-editor-formatter',
  );

  const handleContentChange = (editor: Editor, event: EditorEvent) =>
    onContentChange({
      html: editor.getHTML(),
      plaintext: editor.state.doc.textContent,
      event,
    });

  const tiptapEditor = useEditor({
    extensions: [
      ...noteEditorExtensions,
      EnableNewEditorFormatter
        ? SlashCommand.configure({
            suggestion: {
              items: getSlashMenuSuggestionItems,
              render: () =>
                SlashMenuRenderer({
                  Component: AssemblySlashMenu,
                  ComponentProps: {
                    overrideActions: [
                      'heading1',
                      'heading2',
                      'heading3',
                      'text',
                      'bulletList',
                      'orderedList',
                    ],
                  },
                  tippyProps: {
                    placement: 'bottom-start',
                  },
                }),
            },
          })
        : SlashCommand.configure({
            suggestion: {
              items: getSlashMenuSuggestionItems,
              render: () =>
                SlashMenuRenderer({
                  Component: SlashMenu,
                  tippyProps: {
                    placement: 'bottom-start',
                  },
                }),
            },
          }),
    ],
    editorProps: {
      attributes: {
        class: 'flex flex-col gap-1 mt-1 min-h-[88px]',
      },
    },
    content,
    onCreate: ({ editor }) => handleContentChange(editor, 'create'),
    onUpdate: ({ editor }) => handleContentChange(editor, 'update'),
  });

  if (!tiptapEditor) return null;

  return (
    <div
      className={clsx(
        'rounded border border-solid border-secondary-hover box-border max-h-64 overflow-auto',
        {
          'px-3 py-2': !EnableNewEditorFormatter,
        },
      )}
    >
      {EnableNewEditorFormatter ? (
        <RichTextInput
          classes={{
            container: 'px-3 py-2',
            toolbar: 'rounded-t',
          }}
          editor={tiptapEditor}
          supportActions={{
            toolbar: [
              'bold',
              'italic',
              'underline',
              'strike',
              'bulletList',
              'orderedList',
              'link',
              'heading1',
              'heading2',
              'heading3',
            ],
            highlight: [
              'bold',
              'italic',
              'underline',
              'strike',
              'bulletList',
              'orderedList',
              'link',
            ],
          }}
          onLinkPopoverOpenChange={onLinkPopoverOpenChange}
        >
          <TextareaAutosize
            autoFocus
            className="resize-none border-none box-border p-0 text-lg font-medium w-full placeholder:text-light-gray -mb-1.5"
            placeholder="Untitled"
            name="title"
            value={title}
            onChange={onTitleChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          />
        </RichTextInput>
      ) : (
        <>
          {tiptapEditor && (
            <>
              <TextareaAutosize
                autoFocus
                className="resize-none border-none box-border p-0 text-lg font-medium w-full placeholder:text-light-gray -mb-1.5"
                placeholder="Untitled"
                name="title"
                value={title}
                onChange={onTitleChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />
              <BubbleMenu
                editor={tiptapEditor}
                tippyOptions={{ duration: 100, maxWidth: 'none' }}
              >
                <EditorMenu
                  editor={tiptapEditor}
                  textMenuActions={['heading2', 'heading3', 'text']}
                />
              </BubbleMenu>
            </>
          )}
          <RichTextStyles>
            <EditorContent editor={tiptapEditor} />
          </RichTextStyles>
        </>
      )}
    </div>
  );
};
